import {
  type GetVoiceAgentLanguageSupportQuery,
  VoiceAgentConfigurationModelVoice,
  VoiceAgentLanguage,
  VoiceProvider
} from '~/api/graphql';

export type VoiceAgentLanguageSupportData =
  GetVoiceAgentLanguageSupportQuery['voiceAgentLanguageSupport'];

type VoiceOption = {
  value: VoiceAgentConfigurationModelVoice;
  label: string;
  description?: string;
};

export type LanguageOption = {
  value: VoiceAgentLanguage;
  label: string;
};

// US English is pinned first; everything else is alphabetical by label.
export const voiceAgentLanguageOptions: LanguageOption[] = [
  {
    value: VoiceAgentLanguage.EnUs,
    label: 'English (United States)'
  },
  {
    value: VoiceAgentLanguage.AfZa,
    label: 'Afrikaans (South Africa)'
  },
  { value: VoiceAgentLanguage.ArSa, label: 'Arabic (Saudi Arabia)' },
  {
    value: VoiceAgentLanguage.BgBg,
    label: 'Bulgarian (Bulgaria)'
  },
  {
    value: VoiceAgentLanguage.YueCn,
    label: 'Cantonese (China)'
  },
  { value: VoiceAgentLanguage.CaEs, label: 'Catalan (Spain)' },
  { value: VoiceAgentLanguage.HrHr, label: 'Croatian (Croatia)' },
  { value: VoiceAgentLanguage.CsCz, label: 'Czech (Czechia)' },
  { value: VoiceAgentLanguage.DaDk, label: 'Danish (Denmark)' },
  { value: VoiceAgentLanguage.NlBe, label: 'Dutch (Belgium)' },
  { value: VoiceAgentLanguage.NlNl, label: 'Dutch (Netherlands)' },
  {
    value: VoiceAgentLanguage.EnAu,
    label: 'English (Australia)'
  },
  {
    value: VoiceAgentLanguage.EnIn,
    label: 'English (India)'
  },
  {
    value: VoiceAgentLanguage.EnNz,
    label: 'English (New Zealand)'
  },
  {
    value: VoiceAgentLanguage.EnGb,
    label: 'English (United Kingdom)'
  },
  {
    value: VoiceAgentLanguage.FilPh,
    label: 'Filipino (Philippines)'
  },
  { value: VoiceAgentLanguage.FiFi, label: 'Finnish (Finland)' },
  { value: VoiceAgentLanguage.FrCa, label: 'French (Canada)' },
  { value: VoiceAgentLanguage.FrFr, label: 'French (France)' },
  { value: VoiceAgentLanguage.DeDe, label: 'German (Germany)' },
  { value: VoiceAgentLanguage.ElGr, label: 'Greek (Greece)' },
  { value: VoiceAgentLanguage.HeIl, label: 'Hebrew (Israel)' },
  { value: VoiceAgentLanguage.HiIn, label: 'Hindi (India)' },
  {
    value: VoiceAgentLanguage.HuHu,
    label: 'Hungarian (Hungary)'
  },
  {
    value: VoiceAgentLanguage.IdId,
    label: 'Indonesian (Indonesia)'
  },
  { value: VoiceAgentLanguage.ItIt, label: 'Italian (Italy)' },
  { value: VoiceAgentLanguage.JaJp, label: 'Japanese (Japan)' },
  { value: VoiceAgentLanguage.KnIn, label: 'Kannada (India)' },
  { value: VoiceAgentLanguage.KoKr, label: 'Korean (Korea)' },
  { value: VoiceAgentLanguage.MsMy, label: 'Malay (Malaysia)' },
  { value: VoiceAgentLanguage.ZhCn, label: 'Mandarin (China)' },
  { value: VoiceAgentLanguage.MrIn, label: 'Marathi (India)' },
  {
    value: VoiceAgentLanguage.NoNo,
    label: 'Norwegian (Norway)'
  },
  { value: VoiceAgentLanguage.FaIr, label: 'Persian (Iran)' },
  { value: VoiceAgentLanguage.PlPl, label: 'Polish (Poland)' },
  {
    value: VoiceAgentLanguage.PtBr,
    label: 'Portuguese (Brazil)'
  },
  {
    value: VoiceAgentLanguage.PtPt,
    label: 'Portuguese (Portugal)'
  },
  {
    value: VoiceAgentLanguage.RoRo,
    label: 'Romanian (Romania)'
  },
  { value: VoiceAgentLanguage.RuRu, label: 'Russian (Russia)' },
  { value: VoiceAgentLanguage.SkSk, label: 'Slovak (Slovakia)' },
  {
    value: VoiceAgentLanguage.SlSi,
    label: 'Slovenian (Slovenia)'
  },
  {
    value: VoiceAgentLanguage.Es_419,
    label: 'Spanish (Latin America)'
  },
  { value: VoiceAgentLanguage.EsEs, label: 'Spanish (Spain)' },
  { value: VoiceAgentLanguage.SvSe, label: 'Swedish (Sweden)' },
  { value: VoiceAgentLanguage.TaIn, label: 'Tamil (India)' },
  { value: VoiceAgentLanguage.ThTh, label: 'Thai (Thailand)' },
  { value: VoiceAgentLanguage.TrTr, label: 'Turkish (Turkey)' },
  { value: VoiceAgentLanguage.UkUa, label: 'Ukrainian (Ukraine)' },
  {
    value: VoiceAgentLanguage.ViVn,
    label: 'Vietnamese (Vietnam)'
  }
];

function uniqueLanguages(languages: VoiceAgentLanguage[]): VoiceAgentLanguage[] {
  return Array.from(new Set(languages));
}

function providerLanguages(
  support: VoiceAgentLanguageSupportData,
  provider: VoiceProvider
): Set<VoiceAgentLanguage> {
  return new Set(support.providers.find((entry) => entry.provider === provider)?.languages ?? []);
}

function providerSupportsLanguages(
  support: VoiceAgentLanguageSupportData,
  provider: VoiceProvider,
  languages: VoiceAgentLanguage[]
): boolean {
  const supported = providerLanguages(support, provider);
  return languages.length > 0 && languages.every((language) => supported.has(language));
}

function providerPreferredLanguages(
  support: VoiceAgentLanguageSupportData,
  provider: VoiceProvider
): Set<VoiceAgentLanguage> {
  return new Set(
    support.providers.find((entry) => entry.provider === provider)?.preferredLanguages ?? []
  );
}

function providerDefaultVoice(
  support: VoiceAgentLanguageSupportData,
  provider: VoiceProvider
): VoiceAgentConfigurationModelVoice {
  return (
    support.providers.find((entry) => entry.provider === provider)?.defaultVoice ??
    VoiceAgentConfigurationModelVoice.Grace
  );
}

function voiceSupport(
  support: VoiceAgentLanguageSupportData,
  voice: VoiceAgentConfigurationModelVoice
) {
  return support.voices.find((entry) => entry.voice === voice);
}

function isCustomVoice(
  support: VoiceAgentLanguageSupportData,
  voice: VoiceAgentConfigurationModelVoice
): boolean {
  return voiceSupport(support, voice)?.custom === true;
}

function englishLanguageSet(support: VoiceAgentLanguageSupportData): Set<VoiceAgentLanguage> {
  return new Set(support.englishLanguages);
}

function isAllEnglish(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[]
): boolean {
  if (languages.length === 0) {
    return true;
  }
  const english = englishLanguageSet(support);
  return languages.every((language) => english.has(language));
}

function languagesPreferMiniMax(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[]
): boolean {
  const preferred = providerPreferredLanguages(support, VoiceProvider.Minimax);
  return languages.some((language) => preferred.has(language));
}

// Mirrors minimaxPreferenceApplies in the Go resolver. Where the preference
// applies it beats everything, including an explicitly chosen custom voice,
// so the picker must not offer custom voices for these language sets.
function minimaxPreferenceApplies(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[]
): boolean {
  return (
    languagesPreferMiniMax(support, languages) &&
    providerSupportsLanguages(support, VoiceProvider.Minimax, languages)
  );
}

export function resolveProvider(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[]
): VoiceProvider | undefined {
  if (languages.length === 0) {
    return undefined;
  }
  if (minimaxPreferenceApplies(support, languages)) {
    return VoiceProvider.Minimax;
  }
  if (providerSupportsLanguages(support, VoiceProvider.Cartesia, languages)) {
    return VoiceProvider.Cartesia;
  }
  if (providerSupportsLanguages(support, VoiceProvider.Minimax, languages)) {
    return VoiceProvider.Minimax;
  }
  return undefined;
}

export type VoiceRoute = {
  provider: VoiceProvider;
  voice: VoiceAgentConfigurationModelVoice;
};

export function resolveVoiceRoute(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[],
  voice: VoiceAgentConfigurationModelVoice
): VoiceRoute | undefined {
  const selected = uniqueLanguages(languages);
  if (selected.length === 0) {
    return undefined;
  }

  if (
    isCustomVoice(support, voice) &&
    !minimaxPreferenceApplies(support, selected) &&
    providerSupportsLanguages(support, VoiceProvider.ElevenLabs, selected)
  ) {
    return { provider: VoiceProvider.ElevenLabs, voice };
  }

  const provider = resolveProvider(support, selected);
  if (!provider) {
    return undefined;
  }

  const voiceEntry = voiceSupport(support, voice);
  if (voiceEntry && voiceEntry.provider === provider && !voiceEntry.custom) {
    return { provider, voice };
  }
  return { provider, voice: providerDefaultVoice(support, provider) };
}

function catalogLanguages(support: VoiceAgentLanguageSupportData): Set<VoiceAgentLanguage> {
  const catalog = new Set<VoiceAgentLanguage>();
  for (const provider of [VoiceProvider.Cartesia, VoiceProvider.Minimax]) {
    for (const language of providerLanguages(support, provider)) {
      catalog.add(language);
    }
  }
  return catalog;
}

export function filterLanguageOptions(
  support: VoiceAgentLanguageSupportData,
  languages: LanguageOption[],
  selectedLanguages: VoiceAgentLanguage[],
  voice: VoiceAgentConfigurationModelVoice
): LanguageOption[] {
  const selected = uniqueLanguages(selectedLanguages);
  const selectedSet = new Set(selected);

  let allowed: Set<VoiceAgentLanguage>;
  if (isCustomVoice(support, voice)) {
    // Flash languages keep the custom voice. The MiniMax-preferred languages
    // are offered alongside them even though they drop the clone, so all three
    // behave the same way rather than Cantonese alone looking unsupported.
    allowed = new Set([
      ...providerLanguages(support, VoiceProvider.ElevenLabs),
      ...providerPreferredLanguages(support, VoiceProvider.Minimax)
    ]);
  } else if (isAllEnglish(support, selected)) {
    allowed = catalogLanguages(support);
  } else {
    const provider = resolveProvider(support, selected);
    allowed = provider ? providerLanguages(support, provider) : catalogLanguages(support);
  }

  return languages.filter((option) => selectedSet.has(option.value) || allowed.has(option.value));
}

export function filterVoiceOptions(
  support: VoiceAgentLanguageSupportData,
  voices: VoiceOption[],
  languages: VoiceAgentLanguage[],
  currentVoice?: VoiceAgentConfigurationModelVoice
): VoiceOption[] {
  const selected = uniqueLanguages(languages);
  if (selected.length === 0) {
    return voices;
  }

  const provider = resolveProvider(support, selected);
  const customVoicesAllowed =
    providerSupportsLanguages(support, VoiceProvider.ElevenLabs, selected) &&
    !minimaxPreferenceApplies(support, selected);

  return voices.filter((option) => {
    if (currentVoice && option.value === currentVoice) {
      return true;
    }
    const entry = voiceSupport(support, option.value);
    if (!entry) {
      return false;
    }
    if (entry.custom) {
      return customVoicesAllowed;
    }
    return provider !== undefined && entry.provider === provider;
  });
}

export type LanguageSelectionChange = {
  languages: VoiceAgentLanguage[];
  voice: VoiceAgentConfigurationModelVoice;
};

export function applyLanguageSelectionChange(
  support: VoiceAgentLanguageSupportData,
  previousLanguages: VoiceAgentLanguage[],
  previousVoice: VoiceAgentConfigurationModelVoice,
  requestedLanguages: VoiceAgentLanguage[]
): LanguageSelectionChange {
  const languages = uniqueLanguages(requestedLanguages);
  if (languages.length === 0) {
    return { languages, voice: previousVoice };
  }

  const route = resolveVoiceRoute(support, languages, previousVoice);
  if (!route) {
    // Unreachable via the filtered picker; keep the previous selection intact.
    return { languages: previousLanguages, voice: previousVoice };
  }

  return {
    languages,
    voice: route.voice
  };
}

export type VoiceSelectionChange = {
  voice: VoiceAgentConfigurationModelVoice;
  languages: VoiceAgentLanguage[];
};

export function applyVoiceSelectionChange(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[],
  previousVoice: VoiceAgentConfigurationModelVoice,
  requestedVoice: VoiceAgentConfigurationModelVoice
): VoiceSelectionChange {
  const selected = uniqueLanguages(languages);
  if (selected.length === 0) {
    return { voice: requestedVoice, languages: selected };
  }

  const route = resolveVoiceRoute(support, selected, requestedVoice);
  if (!route) {
    return { voice: previousVoice, languages: selected };
  }

  return {
    voice: route.voice,
    languages: selected
  };
}

export function normalizeLoadedConfiguration(
  support: VoiceAgentLanguageSupportData,
  languages: VoiceAgentLanguage[],
  voice: VoiceAgentConfigurationModelVoice
): { languages: VoiceAgentLanguage[]; voice: VoiceAgentConfigurationModelVoice } {
  const selected = uniqueLanguages(languages);
  const route = resolveVoiceRoute(support, selected, voice);
  if (!route) {
    return { languages: selected, voice };
  }
  return { languages: selected, voice: route.voice };
}
